import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchRaw } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";
import { downloadFile } from "../../lib/download";
import { incidentKeys } from "../incidents/api";

/* Contract-derived types (ADR-S1-005). Hand-authored DTOs are forbidden; every
 * shape below is re-exported from the generated openapi-typescript output. */
export type AttachmentResponse = components["schemas"]["AttachmentResponse"];
export type AttachmentKind = components["schemas"]["AttachmentKind"];
export type CreateAttachmentLinkRequest =
  components["schemas"]["CreateAttachmentLinkRequest"];
export type RemoveAttachmentRequest =
  components["schemas"]["RemoveAttachmentRequest"];

export const attachmentKeys = {
  all: ["attachments"] as const,
  byIncident: (incidentId: string) => ["attachments", incidentId] as const,
  preview: (attachmentId: string) =>
    ["attachments", "preview", attachmentId] as const,
};

/** GET /incidents/{id}/attachments - every slot, removed ones included (F-017). */
export function useAttachments(incidentId: string) {
  return useQuery({
    queryKey: attachmentKeys.byIncident(incidentId),
    queryFn: () =>
      apiFetch<AttachmentResponse[]>(`/incidents/${incidentId}/attachments`),
    enabled: incidentId.length > 0,
  });
}

/** Invalidate the attachment list + the timeline (uploads/removals emit events). */
function invalidateAttachmentViews(
  queryClient: ReturnType<typeof useQueryClient>,
  incidentId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: attachmentKeys.byIncident(incidentId),
  });
  void queryClient.invalidateQueries({
    queryKey: incidentKeys.timeline(incidentId),
  });
}

/**
 * POST /incidents/{id}/attachments (F-006) - multipart upload, 1..10 files.
 * Every file rides in a `files` part; apiFetch skips its JSON content-type
 * default for FormData bodies so the browser sets the multipart boundary.
 */
export function useUploadAttachments(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => {
      const form = new FormData();
      for (const file of files) form.append("files", file, file.name);
      return apiFetch<AttachmentResponse[]>(
        `/incidents/${incidentId}/attachments`,
        { method: "POST", body: form },
      );
    },
    onSuccess: () => invalidateAttachmentViews(queryClient, incidentId),
  });
}

/** POST /incidents/{id}/attachment-links (F-006) - URL + optional description. */
export function useCreateAttachmentLink(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAttachmentLinkRequest) =>
      apiFetch<AttachmentResponse>(`/incidents/${incidentId}/attachment-links`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateAttachmentViews(queryClient, incidentId),
  });
}

export interface RemoveAttachmentVars {
  attachmentId: string;
  reason: string;
}

/** DELETE /attachments/{attachmentId} (F-017, Admin-only) with { reason }. */
export function useRemoveAttachment(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId, reason }: RemoveAttachmentVars) =>
      apiFetch<AttachmentResponse>(`/attachments/${attachmentId}`, {
        method: "DELETE",
        body: JSON.stringify({ reason } satisfies RemoveAttachmentRequest),
      }),
    onSuccess: () => invalidateAttachmentViews(queryClient, incidentId),
  });
}

/**
 * Download an attachment blob (NF-004, ADR-API-06: over the authenticated
 * session, no signed URLs) via the shared download helper (the session
 * cookie + transient object URL). The stored filename doubles as the
 * fallback when the Content-Disposition header is unparseable.
 */
export async function downloadAttachment(
  attachment: Pick<AttachmentResponse, "id" | "filenameOrUrl">,
): Promise<void> {
  await downloadFile(
    `/attachments/${attachment.id}/download`,
    attachment.filenameOrUrl,
  );
}

/**
 * [F-006]/AC-070: an image attachment's content as an object URL, for its
 * preview. Fetched over the authenticated session like the download (NF-004 -
 * an `<img src>` pointing at the api would carry no bearer), cached for the
 * session because a stored blob never changes, and revoked when the preview
 * unmounts. Undefined while loading, when disabled, or when the fetch failed -
 * the row then simply shows no preview.
 */
export function useAttachmentPreview(
  attachmentId: string,
  enabled: boolean,
): string | undefined {
  const blob = useQuery({
    queryKey: attachmentKeys.preview(attachmentId),
    queryFn: async () =>
      (await apiFetchRaw(`/attachments/${attachmentId}/download`)).blob(),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!blob.data) {
      setUrl(undefined);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob.data]);
  return url;
}
